import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import styles from './styles.scoped.css';
import { MultiSelect } from '@mantine/core';
import { itemComponent } from './extendElement';
import { jsonParser } from '../../../utils/builder/common';
export default function MultipleSelect({
  border = true,
  className = '',
  data = [],
  debounce = false,
  defaultValue = [],
  disabled = false,
  eventName = '',
  hideLabel = false,
  input = {},
  inputProps = {},
  isUseStyle = true,
  meta = {},
  name = '',
  onChange = () => { },
  onSearchChange = () => { },
  overflow = 'hidden',
  placeholder = '',
  required = false,
  rightSection = '',
  searchable = false,
  showErrorWithoutTouched = true,
  value = [],
}) {
  const { touched, error } = meta;
  const label = input?.value && !hideLabel;
  const notEmpty = label ? styles['not-empty'] : '';
  const isError = touched && error ? 'isError' : '';
  const [errors, setErrors] = useState();
  const [searchValue, setSearchValue] = useState('');
  const classes = [
    styles['root'],
    className,
    notEmpty,
    isError,
    border ? styles['border'] : '',
  ]
    .filter(Boolean)
    .join(' ');
  useEffect(() => {
    if (touched && error) {
      setErrors(error);
    }
  }, [touched]);

  const handleSearch = (value) => {
    if (debounce) {
      setSearchValue(value);
    } else {
      onSearchChange(value, eventName);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      onSearchChange(searchValue, eventName);
    }, 500); // Debounce delay of 500ms

    return () => {
      clearTimeout(timer);
    };
  }, [searchValue]);

  const sanitizedValue = React.useMemo(() => {
    const parsed = jsonParser(value).length > 0 ? jsonParser(value) : value || [];
    if (Array.isArray(parsed)) {
      return parsed.map(v => v !== null && v !== undefined ? String(v) : '');
    }
    if (parsed !== null && parsed !== undefined && parsed !== '') {
      return [String(parsed)];
    }
    return [];
  }, [value]);

  const hasSelections = React.useMemo(() => {
    return sanitizedValue.some(v => v !== '*' && v !== '');
  }, [sanitizedValue]);

  const sanitizedData = React.useMemo(() => {
    const unique = [];
    const seen = new Set();
    for (const item of (data || [])) {
      if (item && item.value !== undefined && item.value !== null) {
        const valStr = String(item.value);
        if (!seen.has(valStr)) {
          seen.add(valStr);
          unique.push({
            ...item,
            value: valStr,
          });
        }
      }
    }
    return unique;
  }, [data]);

  return (
    <div>
      <MultiSelect
        {...inputProps}
        autoComplete="off"
        className={isUseStyle && classes}
        data={sanitizedData}
        defaultValue={defaultValue || []}
        disabled={disabled}
        error={errors}
        itemComponent={itemComponent}
        name={name}
        onChange={(nextValue) => {
          let cleanedValue = nextValue;
          const prevValue = sanitizedValue;
          const selectedAllNow = nextValue.includes('*') && !prevValue.includes('*');
          const selectedEmptyNow = nextValue.includes('') && !prevValue.includes('');

          if (selectedAllNow) {
            cleanedValue = ['*'];
          } else if (selectedEmptyNow) {
            cleanedValue = [''];
          } else {
            cleanedValue = nextValue.filter(v => v !== '*' && v !== '');
          }

          onChange(cleanedValue);
          input.onChange && input.onChange(cleanedValue);
          setErrors('');
        }}
        onSearchChange={handleSearch}
        placeholder={hasSelections ? '' : placeholder}
        rightSection={rightSection}
        searchable={searchable}
        styles={{
          wrapper: {
            overflow: `${overflow} !important`,
          },
          values: { width: '90%' },
          label: { fontWeight: '600', fontSize: '1rem' },
        }}
        value={sanitizedValue}
        withAsterisk={required}
      />
      {touched && error && <span className={styles.error}>{error}</span>}
      {error && showErrorWithoutTouched && (
        <span className={styles.error}>{error}</span>
      )}
    </div>
  );
}

MultipleSelect.propTypes = {
  border: PropTypes.bool,
  className: PropTypes.string,
  data: PropTypes.array,
  debounce: PropTypes.bool,
  defaultValue: PropTypes.array,
  disabled: PropTypes.bool,
  eventName: PropTypes.string,
  hideLabel: PropTypes.bool,
  input: PropTypes.object,
  inputProps: PropTypes.object,
  isUseStyle: PropTypes.object,
  meta: PropTypes.object,
  name: PropTypes.string,
  onChange: PropTypes.func,
  onSearchChange: PropTypes.func,
  overflow: PropTypes.string,
  placeholder: PropTypes.string,
  required: PropTypes.bool,
  rightSection: PropTypes.node,
  searchable: PropTypes.bool,
  showErrorWithoutTouched: PropTypes.bool,
  value: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
};
